/**
 * 사전 분할(pre-fracture) 생성 (research §3, FR-004a).
 * 순수 기하 로직: 프리미티브의 바운딩 박스를 재귀 랜덤 평면 절단으로 정확히 N개의
 * 볼록 서브박스로 분할한다. three/rapier 비의존(테스트 가능). 렌더러/피직스가 결과로
 * BufferGeometry·collider를 만든다.
 */
import type { PrimitiveSpec } from '../core/types';

export interface ChunkDesc {
  /** 원본 로컬 좌표에서의 청크 중심 오프셋 */
  center: [number, number, number];
  /** 청크 박스 크기 */
  size: [number, number, number];
}

/** 프리미티브 → 바운딩 박스 크기. */
export function boundingBoxOf(spec: PrimitiveSpec): [number, number, number] {
  switch (spec.kind) {
    case 'box':
      return [...spec.size];
    case 'cylinder':
      return [spec.radius * 2, spec.height, spec.radius * 2];
    case 'sphere':
      return [spec.radius * 2, spec.radius * 2, spec.radius * 2];
  }
}

function volume(size: [number, number, number]): number {
  return size[0] * size[1] * size[2];
}

/**
 * 박스를 정확히 count개의 볼록 서브박스로 분할.
 * 매 단계 가장 큰 조각을 최장 축 기준 지터된 위치에서 둘로 나눈다.
 * @param rng 0..1 난수(기본 Math.random) — 테스트 결정성용 주입.
 */
export function fracturePrimitive(
  spec: PrimitiveSpec,
  count: number,
  rng: () => number = Math.random,
): ChunkDesc[] {
  const n = Math.max(1, Math.floor(count));
  const bbox = boundingBoxOf(spec);
  const chunks: ChunkDesc[] = [{ center: [0, 0, 0], size: bbox }];

  while (chunks.length < n) {
    // 가장 부피가 큰 조각 선택.
    let biggestIdx = 0;
    for (let i = 1; i < chunks.length; i++) {
      if (volume(chunks[i].size) > volume(chunks[biggestIdx].size)) biggestIdx = i;
    }
    const piece = chunks[biggestIdx];
    // 최장 축 선택.
    const axis = piece.size[0] >= piece.size[1] && piece.size[0] >= piece.size[2]
      ? 0
      : piece.size[1] >= piece.size[2]
        ? 1
        : 2;
    // 0.35..0.65 지점에서 절단.
    const t = 0.35 + rng() * 0.3;
    const axisLen = piece.size[axis];
    const lenA = axisLen * t;
    const lenB = axisLen - lenA;

    const sizeA: [number, number, number] = [...piece.size];
    const sizeB: [number, number, number] = [...piece.size];
    sizeA[axis] = lenA;
    sizeB[axis] = lenB;

    const centerA: [number, number, number] = [...piece.center];
    const centerB: [number, number, number] = [...piece.center];
    // 절단면 기준 두 조각의 중심 이동.
    centerA[axis] = piece.center[axis] - axisLen / 2 + lenA / 2;
    centerB[axis] = piece.center[axis] + axisLen / 2 - lenB / 2;

    chunks.splice(biggestIdx, 1, { center: centerA, size: sizeA }, { center: centerB, size: sizeB });
  }

  return chunks;
}
